import {AccessRule} from "../model";
import {Feature} from "@luciad/ria/model/feature/Feature.js";
import {Polygon} from "@luciad/ria/shape/Polygon.js";

export enum OverlappingShapesType {
  INCLUDE = "INCLUDE",
  EXCLUDE = "EXCLUDE",
  CLIP = "CLIP",
}

export enum SpatialAreaAcceptType {
  INSIDE = "INSIDE",
  OUTSIDE = "OUTSIDE",
}

export enum PermissionsType {
  READ = "READ",
  UPDATE = "UPDATE",
  DELETE = "DELETE",
  MANAGE = "MANAGE",
}

export interface SecurityFormAccessRule extends AccessRule {
  isNew: boolean;
  scaleRangeEnabled: boolean;
  spatialRangeEnabled: boolean;
  applyToChildren: boolean;
}

export const convertAccessRulesToSecurityFormAccessRules = (accessRules: AccessRule[]): SecurityFormAccessRule[] => {
  return accessRules.map(pp => {
    return {
      ...pp,
      isNew: false,
      scaleRangeEnabled: !!pp.scaleRange,
      spatialRangeEnabled: !!pp.spatialRange,
      applyToChildren: false
    } as SecurityFormAccessRule;
  })
}

export const convertSecurityFormAccessRuleToAccessRule = (securityFormAccessRule: SecurityFormAccessRule): AccessRule => {
  const accessRule = {
    role: securityFormAccessRule.role,
    permissions: securityFormAccessRule.permissions,
  };
  if (securityFormAccessRule.id) {
    accessRule["id"] = securityFormAccessRule.id;
  }
  if (securityFormAccessRule.scaleRangeEnabled && securityFormAccessRule.scaleRange) {
    accessRule["scaleRange"] = {
      minScaleDenominator: securityFormAccessRule.scaleRange.minScaleDenominator,
      maxScaleDenominator: securityFormAccessRule.scaleRange.maxScaleDenominator
    }
  }
  if (securityFormAccessRule.spatialRangeEnabled && securityFormAccessRule.spatialRange) {
    accessRule["spatialRange"] = securityFormAccessRule.spatialRange;
  }
  return accessRule;
}

export const convertSecurityFormAccessRulesToAccessRules = (securityFormAccessRules: SecurityFormAccessRule[]): AccessRule[] => {
  const accessRules = [];
  securityFormAccessRules.forEach((p: SecurityFormAccessRule) => {
    accessRules.push(convertSecurityFormAccessRuleToAccessRule(p));
  });
  return accessRules;
}

export const getCoordinatesFromAreaPolygon = (area: string): number[] => {
  if (area === null) {
    return [];
  }
  const numbers = area.match(/(-?\d*\.?\d+)/g);
  const coordinates = numbers?.map(Number);
  if (coordinates?.length > 2 &&
      coordinates[0] === coordinates[coordinates.length - 2] &&
      coordinates[1] === coordinates[coordinates.length - 1]) {
    coordinates?.splice(coordinates.length - 2, 2);
  }
  return coordinates;
}

export const getAreaPolygonFromCoordinates = (coordinates: number[]): string => {
  let area = null;
  if (coordinates.length >= 2) {
    area = "POLYGON ((";
    for (let i = 0; i < coordinates.length; i += 2) {
      area += `${coordinates[i]} ${coordinates[i + 1]},`;
    }
    area += `${coordinates[0]} ${coordinates[1]}))`;
  }
  return area;
}

export const getPolygonPointsFromFeature = (feature: Feature): number[] => {
  const polygon = feature.shape as Polygon;
  const numPoints = polygon.pointCount;
  const coords: number[] = [];
  for (let i = 0; i < numPoints; i++) {
    const point = polygon.getPoint(i);
    coords.push(point.x);
    coords.push(point.y);
  }
  return coords;
}